import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { ProjectControllerService, ProjectDto } from '@app/generated';

import { UpdateUserDetails } from '@admin/models/update-user-details';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { getProjects, getCurrentUserDetails } from '@dwo/selectors';
import { takeUntil } from 'rxjs/operators';
import { LoadProjects, LoadAllUsers, LoadUserDetails, SaveUser } from '@dwo/actions';
import { UserDetails } from '@admin/models/user-details';
import { buildUserForm } from '@admin/pages/users/edit-user/user-form-builder';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
  standalone: false,
})
export class EditUserComponent implements OnInit, OnDestroy {
  private username: string;
  private subscriptions: Subscription;

  form: UntypedFormGroup;
  projects: ProjectDto[] = [];
  selectedProjects: ProjectDto[] = [];
  selectedProjectIds: string[];
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private store: Store,
    private projectControllerService: ProjectControllerService,
  ) {
    this.subscriptions = new Subscription();
    this.store.dispatch(new LoadAllUsers());
    this.store.dispatch(new LoadProjects());
  }

  ngOnInit() {
    const routeUsername = this.route.snapshot.paramMap.get('username');

    if (!routeUsername) {
      return;
    }

    this.username = routeUsername.trim().toLowerCase();

    this.store.dispatch(new LoadUserDetails(this.username));

    this.projectControllerService.getAuthorizedProjects(this.username).subscribe((authorizedProjects: ProjectDto[]) => {
      this.selectedProjects = authorizedProjects ?? [];
      this.selectedProjectIds = this.selectedProjects.map((p) => p.id);
    });

    this.store
      .select(getProjects)
      .pipe(takeUntil(this.destroy$))
      .subscribe((projects) => {
        this.projects = projects ?? [];
      });

    this.store
      .select(getCurrentUserDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserDetails) => {
        if (user && user.username === this.username) {
          if (this.form && this.form.pristine) {
            return;
          }
          this.form = buildUserForm(this.formBuilder, user);
          const projectPermissionsArray = this.form.get('projectPermissions') as UntypedFormArray;
          projectPermissionsArray.clear();
          (user.permissions.projects || []).forEach((project) => {
            const group = this.formBuilder.group({
              id: [{ value: project.id, disabled: true }, Validators.required],
            });
            projectPermissionsArray.push(group);
          });
        }

        if (this.form?.pristine) {
          return;
        }

        this.form = buildUserForm(this.formBuilder, user);

        const projectPermissionsArray = this.form.get('projectPermissions') as UntypedFormArray;

        projectPermissionsArray.clear();

        (user.permissions.projects || []).forEach((project) => {
          projectPermissionsArray.push(
            this.formBuilder.group({
              id: [{ value: project.id, disabled: true }, Validators.required],
            }),
          );
        });
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private rebuildProjectPermissionsArray() {
    const projectPermissionsArray = this.form.get('projectPermissions') as UntypedFormArray;
    projectPermissionsArray.clear();
    this.selectedProjects.forEach((project) => {
      const group = this.formBuilder.group({
        id: [{ value: project.id, disabled: true }, Validators.required],
      });
      projectPermissionsArray.push(group);
    });
    this.form.markAsDirty();
  }

  onProjectSelect(selectedProjectIds: string[]) {
    this.selectedProjectIds = selectedProjectIds;
    this.selectedProjects = this.projects.filter((p) => selectedProjectIds.includes(p.id));
    this.rebuildProjectPermissionsArray();
  }

  removeProject(projectId: string) {
    this.selectedProjectIds = this.selectedProjectIds.filter((id) => id !== projectId);
    this.selectedProjects = this.projects.filter((p) => this.selectedProjectIds.includes(p.id));
    this.rebuildProjectPermissionsArray();
  }

  onReset() {
    // this.store.dispatch(new LoadUser(this.username));
  }

  onSave() {
    if (!this.form.valid) {
      return;
    }

    const formValue = this.form.getRawValue();
    const data: any = {
      ...formValue,
      name: formValue.fullName,
    };
    delete data.fullName;
    data.projects = this.projectPermissions
      .getRawValue()
      .map((p) => ({ id: p.id }))
      .filter((p) => !!p.id);
    this.store.dispatch(new SaveUser(data));
    this.form.markAsPristine();
  }

  onClose() {
    this.router.navigateByUrl(`/admin/users`);
  }

  get sitePermissions(): UntypedFormArray {
    return this.form.get('sitePermissions') as UntypedFormArray;
  }

  get projectPermissions(): UntypedFormArray {
    return this.form.get('projectPermissions') as UntypedFormArray;
  }

  mapUpdateUserDetailsToUserDetails(updateUserDetails: UpdateUserDetails): UserDetails {
    return {
      username: updateUserDetails.username,
      sapId: updateUserDetails.sapId,
      fullName: updateUserDetails.fullName,
      permissions: {
        sites: updateUserDetails.sites.map((site) => ({
          name: site.name,
          roles: {
            FOREMAN: site.roles.FOREMAN,
            OPERATOR: site.roles.OPERATOR,
          },
        })),
        projects: updateUserDetails.projects.map((project) => ({
          id: project.id,
        })),
      },
    };
  }
}
